import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';
import { DetailedIconPaintRoller, GridContent, GridLayout, Image, Spacer } from '@zillow/constellation';
import { Modal, Paragraph, TextButton } from '@zillow/constellation-10';

import ModalFooter from '../ModalFooter';
import RentalProtectionWhatsCoveredSection from '../../rentGuarantee/ProductIntroIllustrativeV2/components/RentalProtectionWhatsCoveredSection';
import { StyledHidden } from '../../rentGuarantee/StyledComponents';
import MissedRentIcon from '../../../images/rentalProtection/missed-rent.svg';
import PropertyDamageIcon from '../../../images/rentalProtection/property-damage.svg';
import PetDamageIcon from '../../../images/rentalProtection/pet-damage.svg';
import ExcessCleaningIcon from '../../../images/rentalProtection/excess-cleaning.svg';
import EvictionCostsIcon from '../../../images/rentalProtection/eviction-costs.svg';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  coverageSummaryUrl: string;
};

const RentalProtectionWhatsCoveredModal = ({ onClose, onSubmit, coverageSummaryUrl }: Props) => {
  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<ZillowInsuranceServicesLogo />}
      footer={<ModalFooter submitText="Ok" onSubmit={onSubmit} />}
      size={{ base: 'fullScreen', lg: 'lg' }}
      body={
        <div data-testid="RentalProtectionWhatsCoveredModal">
          <StyledHidden breakpoint="md_lte">
            {/* @ts-expect-error */}
            <RentalProtectionWhatsCoveredSection marginBottom={0} isInModal={true} />
            <Paragraph css={{ marginBlock: '600' }}>
              For more information, view a{' '}
              <TextButton asChild>
                <a target="_blank" rel="noopener noreferrer" href={coverageSummaryUrl}>
                  sample coverage summary
                </a>
              </TextButton>
              .
            </Paragraph>
          </StyledHidden>
          <StyledHidden breakpoint="lg_gte">
            <Paragraph css={{ textStyle: 'heading-sm', marginBottom: '200', fontFamily: 'heading-alt' }}>
              What&apos;s covered
            </Paragraph>
            <Paragraph css={{ marginBottom: '400' }}>Examples of potential covered events:</Paragraph>
            <Spacer marginTop="md">
              <GridLayout>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <Image src={MissedRentIcon} width="44" height="44" alt="dollar sign" />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Missed Rent</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    If your renter falls on tough times, coverage may help recoup lost rent.
                  </Paragraph>
                </GridContent>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <Image src={PropertyDamageIcon} width="44" height="44" alt="broken property" />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Property damage</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Claim the cost for damage to carpet, walls, floors, fixtures, and appliances.
                  </Paragraph>
                </GridContent>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <Image src={PetDamageIcon} width="44" height="44" alt="pet paw" />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Pet damage</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Confidently rent to pet owners with coverage for things like carpets or fixtures.
                  </Paragraph>
                </GridContent>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <DetailedIconPaintRoller />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Repaint worn surfaces</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Claim time and materials for repainting significant wear, beyond touch ups.
                  </Paragraph>
                </GridContent>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <Image src={ExcessCleaningIcon} width="44" height="44" alt="trash can" />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Excess cleaning</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    If your renter leaves behind an excessive mess, you may be reimbursed for clean up.
                  </Paragraph>
                </GridContent>
                <GridContent gridColumnEnd="span 2" padding={0} marginBottom="xs">
                  <Image src={EvictionCostsIcon} width="44" height="44" alt="scales of justice" />
                </GridContent>
                <GridContent gridColumnEnd="span 10" padding={0} marginBottom="xs">
                  <Paragraph textStyle="body-bold">Eviction costs</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    In the event of an eviction, proceeding costs and legal fees may be claimed.
                  </Paragraph>
                </GridContent>
              </GridLayout>
            </Spacer>
          </StyledHidden>
        </div>
      }
    />
  );
};

export default RentalProtectionWhatsCoveredModal;
